import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { css } from "styled-system/css";
import { Figure, InlineBar, Ledger, ReviewGraph } from "~/components/charts";
import { api, type ApiResponse } from "~/lib/api";
import { periodWindow, toPeriod } from "~/lib/period";
import { reviewDek, reviewHeadline } from "../story";
import { fmtFixed, inPeriod, periodName } from "~/lib/format";
import { ed, tbl } from "~/components/editorial";

interface ReviewLoadRow {
  reviewer: { id: string; displayName: string; avatarUrl: string | null };
  reviewsGiven: number;
  prsAuthored: number;
}

interface ReviewGraphNode {
  id: string;
  name: string;
  avatarUrl: string | null;
  reviewsGiven: number;
  reviewsReceived: number;
  teamId: string | null;
  teamName: string | null;
}
interface ReviewGraphLink {
  source: string;
  target: string;
  value: number;
}
interface ReviewGraphData {
  nodes: ReviewGraphNode[];
  links: ReviewGraphLink[];
}

// Review Load: who carries the review burden, and where it's lopsided.
export function ReviewLoadTab({ range }: { range: string }) {
  const win = periodWindow(toPeriod(range));
  const now = periodName(win.label);

  const { data, isLoading } = useQuery({
    queryKey: ["review-load", range],
    queryFn: () =>
      api.get<{ data: ReviewLoadRow[] }>(`/code/review-load?range=${range}`),
    staleTime: 60 * 1000,
  });

  const { data: graph } = useQuery({
    queryKey: ["review-graph", range],
    queryFn: () =>
      api.get<ApiResponse<ReviewGraphData>>(
        `/metrics/review-graph?range=${range}`,
      ),
    staleTime: 60 * 1000,
  });

  const rows = useMemo(() => data?.data ?? [], [data]);
  const totalReviews = rows.reduce((s, r) => s + r.reviewsGiven, 0);
  const maxReviews = Math.max(...rows.map((r) => r.reviewsGiven), 1);
  const activeReviewers = rows.filter((r) => r.reviewsGiven > 0).length;
  const topRow = rows.reduce<ReviewLoadRow | null>(
    (b, r) => (r.reviewsGiven > (b?.reviewsGiven ?? 0) ? r : b),
    null,
  );
  const topShare =
    totalReviews > 0 && topRow
      ? Math.round((topRow.reviewsGiven / totalReviews) * 100)
      : 0;

  // Imbalance flag: a single reviewer carrying a disproportionate slice.
  const concentrated = topShare >= 50 && totalReviews >= 20;

  const ratioOf = (row: ReviewLoadRow) =>
    row.prsAuthored > 0
      ? row.reviewsGiven / row.prsAuthored
      : row.reviewsGiven > 0
        ? Infinity
        : 0;
  const isLow = (row: ReviewLoadRow) =>
    ratioOf(row) < 0.5 && row.prsAuthored >= 3;
  const lowGivers = rows.filter(isLow).length;

  return (
    <>
      <div className={ed.kicker}>Review load · {now}</div>
      <h1 className={ed.headlineCompact}>
        {isLoading
          ? "Review load"
          : reviewHeadline(
              totalReviews,
              activeReviewers,
              topRow
                ? { name: topRow.reviewer.displayName, share: topShare }
                : null,
              now,
            )}
      </h1>
      {!isLoading && rows.length > 0 && (
        <p className={ed.dek}>{reviewDek(lowGivers)}</p>
      )}
      <div className={ed.byline}>
        <b>Review load</b> · reviews submitted {inPeriod(win.label)}, by reviewer. From
        GitHub.
      </div>

      <Ledger
        hideMissingPrior
        columns="fit"
        items={[
          { label: "Reviews given", value: fmtFixed(totalReviews) },
          { label: "Active reviewers", value: fmtFixed(activeReviewers) },
          {
            label: "Top reviewer share",
            value: `${topShare}%`,
            note: concentrated
              ? "one person, half or more"
              : topRow?.reviewer.displayName,
            flag: concentrated ? "caution" : undefined,
          },
          {
            label: "Low givers",
            value: fmtFixed(lowGivers),
            note: "under 0.5 reviews per PR",
          },
        ]}
      />

      {concentrated && (
        <p className={ed.alert}>
          One reviewer is handling <span className="num">{topShare}%</span> of
          all reviews. Spread the load to cut bus-factor risk and reviewer
          burnout.
        </p>
      )}

      <div className={ed.sect}>I. By person</div>
      <h2 className={ed.h2}>Who gives reviews, and who gives back</h2>
      {isLoading ? (
        <div className={ed.loading}>Loading review load…</div>
      ) : rows.length === 0 ? (
        <div className={ed.loading}>No review activity in this period.</div>
      ) : (
        <div className={tbl.scroll}>
          <table className={tbl.numeric}>
            <thead>
              <tr>
                <th data-num>#</th>
                <th>Reviewer</th>
                <th data-num>Reviews given</th>
                <th>Share of reviews</th>
                <th data-num>PRs authored</th>
                <th data-num>Give / author</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const share =
                  totalReviews > 0
                    ? Math.round((row.reviewsGiven / totalReviews) * 100)
                    : 0;
                const ratio = ratioOf(row);
                const low = isLow(row);
                return (
                  <tr key={row.reviewer.id}>
                    <td data-num data-dim>
                      {i + 1}
                    </td>
                    <td data-text className={styles.name}>
                      {row.reviewer.displayName}
                    </td>
                    <td data-num>{fmtFixed(row.reviewsGiven)}</td>
                    <td>
                      <InlineBar
                        value={row.reviewsGiven}
                        max={maxReviews}
                        width={120}
                      />
                      <span className={styles.pct}>{share}%</span>
                    </td>
                    <td data-num>{fmtFixed(row.prsAuthored)}</td>
                    <td data-num data-flag={low || undefined}>
                      {ratio === Infinity ? "–" : `${ratio.toFixed(1)}x`}
                      {low && <span className={tbl.flag}>Low</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className={ed.methodNarrow}>
        Give / author is reviews given divided by PRs opened. Below 0.5x, for
        people who opened 3 or more PRs, is flagged as Low: they take more
        review than they give back. A dash means reviews given with no PRs
        authored.
      </p>

      {graph?.data && graph.data.nodes.length > 0 && (
        <>
          <div className={ed.sect}>II. The network</div>
          <h2 className={ed.h2}>Who reviews whom</h2>
          <Figure
            title="Review network"
            sub="Each line joins a reviewer and an author; thicker lines are more reviews."
            method={`${fmtFixed(graph.data.nodes.length)} people and ${fmtFixed(graph.data.links.length)} reviewer-to-author pairs, ${win.label}. Bots excluded. Drag to rearrange.`}
          >
            <ReviewGraph
              nodes={graph.data.nodes}
              links={graph.data.links}
              height={420}
            />
          </Figure>
        </>
      )}
    </>
  );
}

const styles = {
  name: css({ whiteSpace: "nowrap!", fontWeight: 600 }),
  pct: css({
    display: "inline-block",
    width: "40px",
    textAlign: "right",
    color: "ed.ink3",
    ml: "6px",
  }),
};
